import { useState, useEffect, useRef } from "react";
import { Redirect } from "react-router-dom";

import { Box, CircularProgress, Typography } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLock } from "@fortawesome/free-solid-svg-icons";

import AccountAccessLayout from "./layouts/AccountAccessLayout";
import ExplorerSignupSidePanel from "./ExplorerSignupSidePanel";
import Link from "../atoms/mui/Link";
import AccountCreationInput from "../account-access/AccountCreationInput";
import ExplorerScoutOnboarding from "./ExplorerScoutOnboarding";

import { newAuthUser } from "../../helpers/utils/apiCalls";
import { onSignInWithEmailAndPassword } from "../../helpers/components/accountAccess";
import { linkUserToVendorFromParams } from "../../helpers/components/explorerOnboarding";
import { getUrlSearchParams } from "../../helpers/otherHelpers";
import { useAuth } from "../../helpers/firebase";

const getInitialUserDataFromParams = () => {
  const email = getUrlSearchParams("email");
  const name = getUrlSearchParams("name");

  return {
    ...(email ? { email } : {}),
    ...(name ? { name } : {})
  };
};

const readVendorParams = () => {
  const vendorId = getUrlSearchParams("vendorId");
  const buyerId = getUrlSearchParams("buyerId");
  const relId = getUrlSearchParams("relId");
  const role = getUrlSearchParams("role");
  const email = getUrlSearchParams("email");
  const name = getUrlSearchParams("name");
  const teamInvite = getUrlSearchParams("teamInvite");
  const inviteToken = getUrlSearchParams("inviteToken");

  return {
    ...(vendorId ? { vendorId } : {}),
    ...(buyerId ? { buyerId } : {}),
    ...(relId ? { relId } : {}),
    ...(role ? { role } : {}),
    ...(email ? { email } : {}),
    ...(name ? { name } : {}),
    ...(teamInvite === "true" ? { teamInvite: true } : {}),
    ...(inviteToken ? { inviteToken } : {})
  };
};

const TeamInviteLinkingScreen = ({ error }) => (
  <AccountAccessLayout
    title="Joining your team…"
    error={error}
    sidePanel={<ExplorerSignupSidePanel />}
  >
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      gap={2}
      py={4}
    >
      {!error && <CircularProgress />}
      <Typography variant="subtitle1" align="center" color="textSecondary">
        {error
          ? "We couldn't add you to your team automatically."
          : "Hang tight — we're connecting you to your team's Explorer account."}
      </Typography>
    </Box>
  </AccountAccessLayout>
);

const CreateAccount = () => {
  const [redirect, setRedirect] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [userLoading, setUserLoading] = useState(false);
  const [authedUser, setAuthedUser] = useState(null);
  const [existingUser] = useAuth();
  const teamLinkStarted = useRef(false);

  const initialUserData = getInitialUserDataFromParams();
  const vendorParams = readVendorParams();
  const hasVendorParams = !!vendorParams.vendorId;
  const isTeamInvite = !!vendorParams.teamInvite;
  const wizardMode = hasVendorParams ? "invited" : "self_service";
  const wizardUser = authedUser || existingUser;
  const shouldRunWizard =
    !!wizardUser &&
    !isTeamInvite &&
    (hasVendorParams || !!vendorParams.buyerId);

  useEffect(() => {
    if (!wizardUser || !isTeamInvite || teamLinkStarted.current) {
      return;
    }
    teamLinkStarted.current = true;
    const linkTeamMember = async () => {
      const result = await linkUserToVendorFromParams(wizardUser, {
        vendorId: vendorParams.vendorId,
        role: vendorParams.role,
        inviteToken: vendorParams.inviteToken
      });
      if (result?.error) {
        setErrorMsg(
          result.message ||
            "We couldn't add you to your team. Please try again."
        );
        return;
      }
      setRedirect("/explorer/dashboard");
    };
    linkTeamMember();
  }, [wizardUser, isTeamInvite, vendorParams]);

  if (redirect) {
    return <Redirect to={redirect} />;
  }

  if (isTeamInvite && wizardUser) {
    return <TeamInviteLinkingScreen error={errorMsg} />;
  }

  if (existingUser && !userLoading && !shouldRunWizard && !isTeamInvite) {
    return <Redirect to="/explorer/dashboard" />;
  }

  if (shouldRunWizard) {
    return (
      <ExplorerScoutOnboarding
        mode={wizardMode}
        vendorParams={vendorParams}
        onCompleted={() => setRedirect("/explorer/dashboard")}
      />
    );
  }

  const completePasswordEmailAccountCreation = async (inputData) => {
    setUserLoading(true);

    const { error, code, message } = await newAuthUser(inputData);
    if (error) {
      const errorMessageMap = {
        "auth/email-already-exists":
          "This user already exists. Please try logging in instead.",
        "auth/invalid-email": "Please enter a valid email address.",
        "auth/invalid-password": "Password must be at least 6 characters long.",
        "auth/weak-password": "Please choose a stronger password.",
        "auth/invalid-phone-number": "Please enter a valid phone number.",
        "invalid-request": "Please fill out all required fields and try again."
      };
      setErrorMsg(
        errorMessageMap[code] ||
          message ||
          "Something went wrong creating your account. Please try again."
      );
      setUserLoading(false);
      return;
    }

    await onSignInWithEmailAndPassword({
      ...inputData,
      setErrorMsg,
      callback: async (user) => {
        setAuthedUser(user);
        setUserLoading(false);
      }
    });
  };

  const completeOauthAccountCreation = async (user) => {
    setUserLoading(true);
    setAuthedUser(user);
    setUserLoading(false);
  };

  const loginHref = hasVendorParams
    ? `/explorer${window.location.search}`
    : "/explorer";

  const ctaBlock = (
    <Typography variant="subtitle1" align="center">
      Already have an account? <Link href={loginHref}>Login instead.</Link>
    </Typography>
  );

  const layoutFooter = (
    <Box display="flex" alignItems="center" gap={1}>
      <FontAwesomeIcon icon={faLock} style={{ fontSize: 14 }} />
      <Typography variant="body2" color="textSecondary">
        Your data is secure with us. Learn more in our{" "}
        <Link href="https://www.aclymate.com/privacy-policy">
          Privacy Policy
        </Link>
        .
      </Typography>
    </Box>
  );

  return (
    <AccountAccessLayout
      title="Create Your Aclymate Explorer Account"
      error={errorMsg}
      sidePanel={<ExplorerSignupSidePanel />}
      footer={layoutFooter}
    >
      <AccountCreationInput
        isNewCompanyAdmin={hasVendorParams}
        setErrorMsg={setErrorMsg}
        userLoading={userLoading}
        initialUserData={initialUserData}
        completePasswordEmailAccountCreation={
          completePasswordEmailAccountCreation
        }
        completeOauthAccountCreation={completeOauthAccountCreation}
        oauthStacked
        cta={ctaBlock}
      />
    </AccountAccessLayout>
  );
};
export default CreateAccount;
